import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import AxiosInstance from '../../Helper/AxiosInstance'
import { toast } from 'react-toastify'
import { C, fOutfit, fBody, LogoMark, Wordmark } from '../../Shared/Brand'
import { Context } from '../../Helper/Context'

const CLOUDFLARE_SITE_KEY = (process.env.REACT_APP_SITE_KEY || "").trim()

const styles = `
.xp-login { font-family: ${fBody}; }
.xp-login a { color: inherit; text-decoration: none; }
.xp-input {
  width: 100%; background: rgba(255,255,255,0.04); border: 1px solid ${C.dbdr}; color: ${C.ltx};
  border-radius: 10px; padding: 12px 14px; font-size: 14px; font-family: ${fBody}; transition: border-color 0.2s, background 0.2s;
}
.xp-input::placeholder { color: ${C.ltx3}; }
.xp-input:focus { outline: none; border-color: ${C.primary}; background: rgba(255,255,255,0.06); }
.xp-cta { transition: background 0.2s; }
.xp-cta:hover { background: ${C.primaryDark}; }
.xp-cta:disabled { opacity: 0.6; cursor: not-allowed; }
.xp-back:hover { color: ${C.ltx}; }
.xp-check { accent-color: ${C.primary}; width: 15px; height: 15px; }
@keyframes xp-spin { to { transform: rotate(360deg); } }
.xp-spinner { animation: xp-spin 0.7s linear infinite; }
@media (max-width: 900px) {
  .xp-login-left { display: none !important; }
}
`

export default function Login() {
  const navigate = useNavigate()
  const { verifyAuth } = Context()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState({ username: "", password: "" })
  const [rememberMe, setRememberMe] = useState(false)
  const [cfToken, setCfToken] = useState("")
  const [cfStatus, setCfStatus] = useState({ state: 'loading', message: '' })
  const [loginError, setLoginError] = useState('')
  const cfWidgetRef = useRef(null)
  const cfWidgetIdRef = useRef(null)

  useEffect(() => {
    const remembered = localStorage.getItem('rememberMe') === 'true'
    const rememberedUsername = remembered ? localStorage.getItem('rememberedUsername') : ""
    const rememberedPassword = remembered ? localStorage.getItem('rememberedPassword') : ""
    setRememberMe(remembered)
    setData((prev) => ({
      ...prev,
      username: rememberedUsername || "",
      password: rememberedPassword || ""
    }))
  }, [])

  useEffect(() => {
    if (window.turnstile) {
      renderCloudflareWidget()
      return
    }
    const timeoutId = setTimeout(() => {
      setCfStatus((s) => s.state === 'loading'
        ? { state: 'error', message: 'Verification widget failed to load (timed out after 8s). Check your network or any ad-blocker/privacy extension, then refresh.' }
        : s)
    }, 8000)
    const script = document.createElement("script")
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js"
    script.async = true
    script.defer = true
    script.onload = () => { clearTimeout(timeoutId); renderCloudflareWidget() }
    script.onerror = () => {
      clearTimeout(timeoutId)
      setCfStatus({ state: 'error', message: 'Could not load the verification script from Cloudflare. Check your network or any ad-blocker/privacy extension, then refresh.' })
    }
    document.body.appendChild(script)
    return () => {
      clearTimeout(timeoutId)
      if (script.parentNode) {
        script.parentNode.removeChild(script)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderCloudflareWidget = () => {
    if (!CLOUDFLARE_SITE_KEY) {
      setCfStatus({ state: 'error', message: 'Verification is not configured (missing site key). Contact support.' })
      return
    }
    if (window.turnstile && cfWidgetRef.current && cfWidgetIdRef.current === null) {
      try {
        const widgetId = window.turnstile.render(cfWidgetRef.current, {
          sitekey: CLOUDFLARE_SITE_KEY,
          callback: function (token) {
            setCfToken(token)
            setCfStatus({ state: 'verified', message: '' })
          },
          'error-callback': function (errorCode) {
            setCfToken("")
            if (String(errorCode) === "600010") {
              setCfStatus({ state: 'error', message: 'Verification widget is not registered for this domain (Cloudflare error 600010). Contact support.' })
              return
            }
            setCfStatus({ state: 'error', message: `Verification failed (Cloudflare error ${errorCode}). Please refresh and try again.` })
          },
          'expired-callback': function () {
            setCfToken("")
            setCfStatus({ state: 'ready', message: '' })
          },
          theme: 'dark'
        })
        cfWidgetIdRef.current = widgetId
        setCfStatus({ state: 'ready', message: '' })
      } catch (e) {
        setCfStatus({ state: 'error', message: 'Verification widget failed to initialize: ' + (e?.message || 'unknown error') })
      }
    }
  }

  const handleChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value })
    if (loginError) setLoginError('')
  }

  const handleRememberMeChange = (e) => {
    setRememberMe(e.target.checked)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoginError('')
    if (!data.username.trim() || !data.password.trim()) {
      setLoginError('Username and password are required.')
      return
    }
    if (!cfToken) {
      setLoginError('Please wait for verification to finish (see status above), then try again.')
      return
    }
    try {
      setLoading(true)
      const res = await AxiosInstance.post('/users/login', {
        username: data.username,
        password: data.password,
        cf_token: cfToken
      })

      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true')
        localStorage.setItem('rememberedUsername', data.username)
        localStorage.setItem('rememberedPassword', data.password)
      } else {
        localStorage.removeItem('rememberMe')
        localStorage.removeItem('rememberedUsername')
        localStorage.removeItem('rememberedPassword')
      }

      if (res && res.success === true && res.token) {
        localStorage.setItem('token', res.token)
        verifyAuth()
        setData({ username: "", password: "" })
        navigate('/user/bot')
      } else {
        setLoginError(res.message || 'Login failed.')
      }
    } catch (err) {
      // AxiosInstance's response interceptor already shows a toast, but that
      // can be missed — always also show a persistent inline message here,
      // pulled from the same real backend field.
      const backendMessage = err?.response?.data?.message || err?.response?.data?.mess
      setLoginError(backendMessage || 'Something went wrong reaching the server. Please try again.')
    } finally {
      setLoading(false)
      if (window.turnstile && cfWidgetIdRef.current !== null) {
        window.turnstile.reset(cfWidgetIdRef.current)
      }
      setCfToken("")
    }
  }

  return (
    <div className="xp-login" style={{ minHeight: '100vh', display: 'flex' }}>
      <style>{styles}</style>

      {/* LEFT — full-bleed brand artwork */}
      <div className="xp-login-left" style={{
        flex: 1, minHeight: '100vh', background: C.dark,
        backgroundImage: 'url(/assets/images/login/login-hero.webp)',
        backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
      }} />

      {/* RIGHT — the form */}
      <div style={{ flex: 1, minWidth: '360px', minHeight: '100vh', background: C.dark, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 32px' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <LogoMark size={26} />
            <Wordmark size={14} />
          </Link>
          <Link to="/" className="xp-back" style={{ fontSize: '13px', color: C.ltx2, transition: 'color 0.2s' }}>← Back to home</Link>
        </div>

        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 32px 40px' }}>
          <div style={{ width: '100%', maxWidth: '380px' }}>
            <h2 style={{ fontFamily: fOutfit, fontWeight: 700, fontSize: '24px', color: C.ltx, marginBottom: '6px' }}>Sign into Your Account</h2>
            <p style={{ fontSize: '13px', color: C.ltx2, marginBottom: '28px' }}>Sign in to access your dashboard and manage your account.</p>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: C.ltx2, marginBottom: '6px', fontWeight: 500 }}>Username</label>
                <input
                  type="text"
                  className="xp-input"
                  name="username"
                  value={data.username}
                  placeholder="Enter username"
                  onChange={handleChange}
                  required
                  autoComplete="username"
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: C.ltx2, marginBottom: '6px', fontWeight: 500 }}>Password</label>
                <input
                  type="password"
                  className="xp-input"
                  name="password"
                  value={data.password}
                  placeholder="Enter password"
                  onChange={handleChange}
                  required
                  autoComplete="current-password"
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="rememberMe"
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                    className="xp-check"
                  />
                  <label htmlFor="rememberMe" style={{ fontSize: '13px', color: C.ltx2 }}>Remember Me</label>
                </div>
                <a href="https://bittronexa.com/login" style={{ fontSize: '13px', color: C.primary, fontWeight: 600 }}>Forgot password?</a>
              </div>
              <p style={{ fontSize: '12px', color: C.ltx3, marginBottom: '18px', lineHeight: 1.6 }}>
                Use your Bittronexa account username and password. Manage your password from the <a href="https://bittronexa.com/login" style={{ color: C.ltx2, textDecoration: 'underline' }}>Bittronexa dashboard</a>.
              </p>

              <div style={{ marginBottom: '8px' }} ref={cfWidgetRef} />
              {cfStatus.state === 'loading' && (
                <p style={{ fontSize: '11px', color: C.ltx3, marginBottom: '18px' }}>Loading verification…</p>
              )}
              {cfStatus.state === 'error' && (
                <p style={{ fontSize: '11px', color: C.down, marginBottom: '18px', lineHeight: 1.6 }}>⚠ {cfStatus.message}</p>
              )}

              {loginError && (
                <div style={{ background: 'rgba(240,96,96,0.12)', border: `1px solid ${C.down}`, borderRadius: '10px', padding: '12px 14px', marginBottom: '18px' }}>
                  <p style={{ fontSize: '13px', color: C.down, fontWeight: 600, margin: 0 }}>⚠ {loginError}</p>
                </div>
              )}

              <button
                className="xp-cta"
                type="submit"
                disabled={loading}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  background: C.primary, color: C.dark, fontWeight: 700, fontSize: '14px', padding: '13px 0',
                  borderRadius: '999px', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? (
                  <span className="xp-spinner" style={{ width: '16px', height: '16px', border: `2px solid ${C.dark}`, borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block' }} />
                ) : "Login"}
              </button>
            </form>
          </div>
        </div>

        <p style={{ fontSize: '10px', color: C.ltx3, textAlign: 'center', padding: '0 32px 24px', lineHeight: 1.6 }}>
          Trading involves risk and may result in losses exceeding your deposit. Not financial advice.
        </p>
      </div>
    </div>
  )
}
